import { useContext, useEffect, useRef, useState } from 'react';

import { CurrProfileContext } from '../App/App.tsx';
import { supabase } from '../supabaseClient';
import { Avatar, Box, Button, Modal, TextField, Typography } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { compressImageToJpeg } from '../CompressImage.tsx';
import { JamPadProfile } from '../DataModels.ts';

interface Props {
    profile: JamPadProfile | undefined,
    setProfile: any
}
export default function EditProfile({ profile, setProfile } : Props) {
    const maxPfpSize = 110; // in kB
    const { profile: currUserProfile, setProfile: setCurrUserProfile } = useContext(CurrProfileContext);
    const [open, setOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [newData, setNewData] = useState({
        pfp: profile?.pfp, display_name: profile?.display_name, bio: profile?.bio
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null); // new pfp
    const [canSave, setCanSave] = useState(false);

    useEffect(() => {
        setNewData({ pfp: profile?.pfp, display_name: profile?.display_name, bio: profile?.bio });
    }, [open]);
    useEffect(() => {
        setCanSave(canSaveChanges());
    }, [newData]);

    function canSaveChanges() {
        return (newData.display_name?.length ?? 0) > 0 &&
            (newData.display_name?.length ?? 0) < 20 && 
            (newData.bio?.length ?? 0) < 120 && (
                newData.pfp !== profile?.pfp ||
                newData.bio !== profile?.bio ||
                newData.display_name !== profile?.display_name
            );
    }

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            compressImageToJpeg(e.target.files[0], maxPfpSize).then((compressed) => {
                setNewData({ ...newData, pfp: URL.createObjectURL(compressed) });
                setSelectedFile(compressed);
            });
        }
    };

    async function saveProfile() {
        if(newData.pfp !== profile?.pfp && newData.pfp !== null && selectedFile) {
            console.log(selectedFile, newData.pfp);
            const filePath = `${profile?.id.toUpperCase()}.jpeg`
            await supabase.storage.from("profiles").remove([filePath]);
            const {error: uploadError} = await supabase.storage
                .from("profiles")
                .upload(filePath, selectedFile);
            console.log(filePath, uploadError);
            if (uploadError) {
                console.error("Failed to upload pfp: " + uploadError.message);
                alert("Could not upload profile picture.");
                return;
            }
            const {data: image} = supabase.storage.from("profiles").getPublicUrl(filePath);

            // make sure to use new version, not cached image
            const version = Date.now();
            newData.pfp = `${image.publicUrl}?v=${version}`;
        }
        console.log(newData.pfp);
        
        const { error } = await supabase.from('profiles')
            .update(newData)
            .eq("id", profile?.id);
        if(error) {
            console.error("Failed to update profile: " + error.message);
        } else {
            setProfile({ ...profile, pfp: newData.pfp, 
                display_name: newData.display_name, bio: newData.bio
            });
            setCurrUserProfile({ ...currUserProfile, pfp: newData.pfp, 
                display_name: newData.display_name, bio: newData.bio
            });
            setOpen(false);
        }
    }

    return <div>
        <Button variant='contained' color='info'
            sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => {setOpen(true)}}
        ><Edit/><div>Edit Profile</div></Button>

        <Modal open={open} onClose={() => {setOpen(false)}}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '74vw', p: '24px 40px',
                bgcolor: 'white', borderRadius: '4px',
                display: 'flex', flexDirection: 'column', 
                justifyContent: 'center', gap: '26px'
            }}>
                <div style={{ display: 'flex' }}>
                    <Typography fontSize='32px' fontWeight='bold'>
                        Edit Profile
                    </Typography>
                    <div style={{ flex: '1' }}></div>
                    <Button onClick={() => {
                        if(!canSaveChanges() || window.confirm("You have unsaved changes. Close anyway?")) {
                            setOpen(false);
                        }
                    }} 
                    color='error'>Cancel</Button>
                </div>

                <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                 }}>
                    {/* Actual image uploader */}
                    <input onChange={handleFileChange} multiple={false} ref={fileInputRef} type='file' hidden/>

                    <Avatar onClick={() => fileInputRef.current?.click() }
                        style={{ width: '24vh', height: '24vh' }} src={newData.pfp ?? ""} />
                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Button onClick={() => fileInputRef.current?.click() } 
                            variant='contained' color='info' style={{
                            display: 'flex', alignItems: 'center', gap: '8px'
                        }}>
                            <Edit/>
                            <div>Edit Profile Photo</div>
                        </Button>
                        <Button onClick={() => {
                            setNewData({...newData, pfp: null });
                            setSelectedFile(null); // clear any prev upload
                        }} variant='contained' color='error' style={{
                            display: 'flex', alignItems: 'center', gap: '8px'
                        }}>
                            <Delete/>
                            <div>Remove Photo</div>
                        </Button>
                    </div>
                </Box>

                <TextField label="Display Name" onChange={(e) => {
                    setNewData({...newData, display_name: e.target.value})
                }} value={newData?.display_name} placeholder='Enter new display name...'/>

                <TextField label="Bio (max 120 char.s)" onChange={(e) => {
                    setNewData({...newData, bio: e.target.value})
                }} value={newData?.bio} placeholder='Enter new bio...'/>
                
                <Button size='large' onClick={() => saveProfile() } 
                    disabled={!canSave}>Save Changes</Button>
            </Box>
        </Modal>
    </div>;
}